import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GoogleMap, useLoadScript, OverlayView } from '@react-google-maps/api';
import { Link } from 'react-router-dom';
import { apiGet } from '../lib/api';
import { useCurrency } from '../context/CurrencyContext';
import logger from '../lib/logger';
import { FiUser } from 'react-icons/fi';
import { slugify, initialsOf } from '../lib/slug';
import { getPublicDisplayName } from '../utils/displayName';

const mapContainerStyle = { width: '100%', height: '600px' };
const defaultCenter = { lat: 55.6122406, lng: 12.9995841 }; // Malmö

// Google-only implementation: Leaflet removed

// Helper to compose a full address from available fields
function formatFullAddress(t) {
  // Map ISO country codes to full names
  const COUNTRY_NAME_BY_CODE = {
    SE: 'Sweden', NO: 'Norway', DK: 'Denmark', FI: 'Finland', IS: 'Iceland',
    DE: 'Germany', NL: 'Netherlands', BE: 'Belgium', FR: 'France', ES: 'Spain',
    IT: 'Italy', PT: 'Portugal', GB: 'United Kingdom', IE: 'Ireland', US: 'United States',
    CA: 'Canada', AU: 'Australia', NZ: 'New Zealand', CH: 'Switzerland', AT: 'Austria',
    PL: 'Poland', CZ: 'Czechia', SK: 'Slovakia', HU: 'Hungary', EE: 'Estonia', LT: 'Lithuania', LV: 'Latvia'
  };

  const normalizeCountry = (val) => {
    const raw = String(val || '').trim();
    const code = raw.toUpperCase();
    return COUNTRY_NAME_BY_CODE[code] || raw;
  };

  // If backend provides a full address, normalize country code within it and dedupe
  const rawFull = t?.full_address || t?.location?.full_address;
  if (rawFull) {
    const parts = String(rawFull)
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean)
      .map((p) => (/^[A-Za-z]{2}$/.test(p) ? normalizeCountry(p) : p));
    const seen = new Set();
    const unique = parts.filter((p) => {
      const k = p.toLowerCase();
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    return unique.join(', ');
  }

  // Compose from discrete fields: street, city, postal code, country (prefer name)
  const street = t?.address || t?.address_line || t?.location?.address || t?.location?.address_line || t?.user?.address_line || '';
  const city = t?.city?.name || t?.city_name || t?.location?.city?.name || t?.city || '';
  const postal = t?.postal_code || t?.user?.postal_code || '';
  const countryRaw = t?.country?.name || t?.location?.country || t?.country || t?.country_code || t?.user?.country_code || '';
  const country = normalizeCountry(countryRaw);

  const parts = [street, city, postal, country]
    .map((p) => String(p || '').trim())
    .filter(Boolean);
  const seen = new Set();
  const unique = parts.filter((p) => {
    const k = p.toLowerCase();
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  return unique.join(', ');
}

function PriceTag({ creative, isSelected, onClick, formatAmount }) {
  const label = creative.priceLabel || (creative.hourlyRate ? formatAmount(creative.hourlyRate, creative.currency) : '?');
  return (
    <OverlayView
      position={creative.position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={(w, h) => ({ x: -(w / 2), y: -(h + 5) })}
    >
      <div className="relative group z-100">
        <div
          onClick={onClick}
          title={creative.name || ''}
          className={`${isSelected ? 'bg-[#1756fd] text-white scale-110' : 'bg-card-bg text-primary-text'} min-w-[45px] inline-flex items-center justify-center px-3 py-1.5 rounded-full font-medium shadow-md border border-card-border transform transition-all duration-200 cursor-pointer whitespace-nowrap hover:scale-105 hover:shadow-lg`}
        >
          {label}
        </div>
        {/* Hover tooltip with creative name and avatar */}
        <div className="absolute -top-16 left-1/2 -translate-x-1/2 px-2 py-2 rounded-lg shadow-lg border border-card-border bg-card-bg flex items-center gap-2 opacity-0 group-hover:opacity-100 pointer-events-none transition">
          <img src={creative.avatar || '/placeholder.svg'} alt={creative.name || 'Creative'} className="w-8 h-8 rounded-full object-cover border border-card-border" />
          <span className="text-xs md:text-sm text-primary-text whitespace-nowrap max-w-[160px] truncate">{creative.name || 'Creative'}</span>
        </div>
      </div>
    </OverlayView>
  );
}

// Leaflet helpers removed

// Parse an address string into structured parts for geocoding
function parseAddressParts(input) {
  const raw = String(input || '').trim();
  const parts = raw.split(',').map((p) => p.trim()).filter(Boolean);
  const street = parts[0] || '';
  // Identify postal code token
  const postal = parts.find((p) => /^\d{3,6}$/.test(p)) || '';
  // Find the first token that looks like a city (letters, not floor/apartment markers)
  const isNonCityToken = (p) => /^(\d+\.?\s*(sal|tr|floor|apt|lägenhet))$/i.test(p);
  const cityCandidate = parts.slice(1).find((p) => /[A-Za-z]/.test(p) && !isNonCityToken(p)) || '';
  const city = cityCandidate.replace(/\b\d{3,}\s*$/u, '').trim();
  return { street, city, postal };
}

// Helper to format a single creative object from the API response
function formatCreative(t) {
  const latRaw = t.latitude ?? t.location?.latitude ?? t.user?.latitude;
  const lngRaw = t.longitude ?? t.location?.longitude ?? t.user?.longitude;
  const lat = Number(latRaw);
  const lng = Number(lngRaw);
  const displayName = getPublicDisplayName(t.user) || null;
  const verification_status = t.verification_status || t.verificationStatus || '';
  const verification_badge = Boolean(t.verification_badge ?? t.verificationBadge ?? false);
  const isVerified = verification_badge || ['ai_verified', 'admin_approved'].includes(String(verification_status));

  return {
    id: t.id,
    user: t.user,
    name: displayName || t.title || 'Creative',
    title: t.title || t.user?.title || '',
    avatar: t.user?.avatar_url || t.user?.avatar || '/placeholder.svg',
    rating: t.rating ?? 0,
    hourlyRate: t.hourly_rate ?? t.hourlyRate,
    currency: t.currency || 'SEK',
    position: (Number.isFinite(lat) && Number.isFinite(lng)) ? { lat, lng } : undefined,
    skills: (t.skills || []).map((s) => s.name || s),
    location: t.location?.name || t.location || '',
    address: formatFullAddress(t),
    bio: t.bio || t.user?.bio || t.profile?.bio || t.description || '',
    services: (t.services || []).map((s) => s.name || s.title || s),
    servicesCount: Array.isArray(t.services) ? t.services.length : 0,
    isVerified,
    verification_status,
  };
}

export default function CreativeMap({ apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '' }) {
  const { t } = useTranslation();
  const { formatPrice, convert, currency: userCurrency } = useCurrency();
  const { isLoaded, loadError } = useLoadScript({ googleMapsApiKey: apiKey, libraries: ['places'] });
  const hasApiKey = Boolean(apiKey && apiKey.trim());
  const [mapsError, setMapsError] = useState('');

  // Convert and format currency using user's preferred currency
  const formatAmount = useCallback((amount, sourceCurrency = 'SEK') => {
    const converted = convert(amount, sourceCurrency, userCurrency);
    return formatPrice(converted, userCurrency);
  }, [convert, formatPrice, userCurrency]);

  const [creatives, setCreatives] = useState([]);
  const [selectedCreative, setSelectedCreative] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [mapBounds, setMapBounds] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [skillsOptions, setSkillsOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const categoryById = useMemo(() => {
    const m = new Map();
    (Array.isArray(categoryOptions) ? categoryOptions : []).forEach((c) => { if (c && c.id != null) m.set(String(c.id), c); });
    return m;
  }, [categoryOptions]);
  const parentCategories = useMemo(() => {
    return (Array.isArray(categoryOptions) ? categoryOptions : []).filter((c) => !c.parent_id);
  }, [categoryOptions]);
  const childrenByParentId = useMemo(() => {
    const m = new Map();
    (Array.isArray(categoryOptions) ? categoryOptions : []).forEach((c) => {
      const pid = c.parent_id;
      if (!pid) return;
      const key = String(pid);
      const arr = m.get(key) || [];
      arr.push(c);
      m.set(key, arr);
    });
    return m;
  }, [categoryOptions]);
  // Removed scope dropdown (All/Countries/Cities)

  // Hierarchical location selection
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState('');
  const [selectedCityName, setSelectedCityName] = useState('');
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [precisePositions, setPrecisePositions] = useState({});
  const [geocodingBusy, setGeocodingBusy] = useState(false);
  // Prevent auto-centering from overriding user-initiated centers (search, city pick, my location)
  const [centerLockedUntil, setCenterLockedUntil] = useState(0);

  // Filters state
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [minRating, setMinRating] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedParentCategoryId, setSelectedParentCategoryId] = useState('');
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState('');
  // Verified is a condition, not a toggle
  // Local UI state for enhanced filters
  const [skillSearchTerm, setSkillSearchTerm] = useState('');

  const mapRef = useRef(null);
  // Removed Leaflet map ref

  const onMapLoad = useCallback((map) => { mapRef.current = map; setMapBounds(map.getBounds()); }, []);
  const onBoundsChanged = useCallback(() => {
    if (!mapRef.current) return;
    const b = mapRef.current.getBounds();
    if (b) setMapBounds({
      north: b.getNorthEast().lat(),
      south: b.getSouthWest().lat(),
      east: b.getNorthEast().lng(),
      west: b.getSouthWest().lng(),
    });
  }, []);

  useEffect(() => {
    if (!loadError) return;
    let msg = '';
    try {
      if (loadError.message) msg = loadError.message;
      else if (typeof Event !== 'undefined' && loadError instanceof Event) {
        const src = loadError?.target?.src || '';
        msg = src ? `Failed to load ${src}` : 'Failed to load Google Maps script';
      } else {
        msg = String(loadError);
      }
    } catch (e) {
      void e;
      msg = 'Failed to load Google Maps';
    }
    setMapsError(msg);
  }, [loadError]);

  useEffect(() => {
    const handler = () => setMapsError('Google Maps authentication failed');
    try { window.gm_authFailure = handler; } catch (e) { void e; }
    return () => { try { delete window.gm_authFailure; } catch (e) { void e; } };
  }, []);

  // Removed Leaflet creation handler

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        setLoading(true);
        const res = await apiGet('/creatives', { verified: true }); // Fetch creatives once; filter client-side
        const creativesList = res?.data?.creatives || res?.data || res || [];
        const formatted = (Array.isArray(creativesList) ? creativesList : []).map(formatCreative);
        const augmented = await augmentCreatives(formatted);
        if (mounted) {
          setCreatives(augmented);
          // If we have a creative with coordinates, center the map there to ensure markers appear
          const firstWithCoords = augmented.find((x) => x.position && typeof x.position.lat === 'number' && typeof x.position.lng === 'number');
          if (firstWithCoords) {
            setMapCenter(firstWithCoords.position);
          }
        }
      } catch (e) {
        logger.error('Failed to load creatives for map', e);
      } finally {
        setLoading(false);
      }
    };
    run();
    return () => { mounted = false; };
  }, []);

  // Removed periodic auto-refresh to keep user-centered map stable

  // Client-side filtering only: no server refetch on search/filters
  // Creatives are fetched once on mount and augmented; filtering happens in useMemo

  // Removed bounds-based refetch: rely on unified fetch and relaxed visibility

  // Load filter options from APIs when opening the off-canvas panel
  useEffect(() => {
    let active = true;
    async function fetchFilters() {
      if (!isFilterOpen) return;
      // Avoid refetch if already loaded
      if (skillsOptions.length > 0 && categoryOptions.length > 0) return;
      try {
        setLoadingFilters(true);
        const skillsRes = await apiGet('/skills');
        const skillList = skillsRes?.data?.skills || skillsRes?.skills || skillsRes?.data || [];
        const categoriesRes = await apiGet('/categories');
        const categoryList = categoriesRes?.data?.categories || categoriesRes?.categories || categoriesRes?.data || [];
        if (active) {
          setSkillsOptions(Array.isArray(skillList) ? skillList : []);
          setCategoryOptions(Array.isArray(categoryList) ? categoryList : []);
        }
      } catch (err) {
        logger.warn('Failed to fetch filter options', err);
      } finally {
        setLoadingFilters(false);
      }
    }
    fetchFilters();
    return () => { active = false; };
  }, [isFilterOpen, skillsOptions.length, categoryOptions.length]);

  // Fetch hierarchical countries and cities
  useEffect(() => {
    let active = true;
    async function fetchCountries() {
      try {
        setLoadingCountries(true);
        const res = await apiGet('/countries');
        const countryList = res?.data?.countries || res?.countries || res?.data || [];
        if (active) setCountries(Array.isArray(countryList) ? countryList : []);
      } catch (err) {
        logger.warn('Failed to fetch countries', err);
      } finally {
        setLoadingCountries(false);
      }
    }
    fetchCountries();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    async function fetchCities() {
      if (!selectedCountryCode) { setCities([]); return; }
      try {
        setLoadingCities(true);
        const res = await apiGet('/cities', { country: selectedCountryCode });
        const cityList = res?.data?.cities || res?.cities || res?.data || [];
        if (active) setCities(Array.isArray(cityList) ? cityList : []);
      } catch (err) {
        logger.warn('Failed to fetch cities', err);
      } finally {
        setLoadingCities(false);
      }
    }
    fetchCities();
    return () => { active = false; };
  }, [selectedCountryCode]);

  // Geocode on search
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) return;
    const timer = setTimeout(() => {
      const looksLikeAddress = /\d/.test(q) || q.includes(',');
      // Prefer structured geocoding for street-level queries
      if (looksLikeAddress) {
        const parsed = parseAddressParts(q);
        const params = { street: parsed.street };
        const cityGuess = parsed.city || '';
        const cityParam = selectedCityName || cityGuess;
        if (cityParam) params.city = cityParam;
        if (selectedCountryCode) params.countrycodes = String(selectedCountryCode).toLowerCase();
        if (mapBounds) {
          const viewbox = `${mapBounds.west},${mapBounds.north},${mapBounds.east},${mapBounds.south}`;
          params.viewbox = viewbox;
          params.bounded = '1';
        }
        geocodeAndCenter(params);
      } else {
        const params = { q };
        if (selectedCountryCode) params.countrycodes = String(selectedCountryCode).toLowerCase();
        if (mapBounds) {
          const viewbox = `${mapBounds.west},${mapBounds.north},${mapBounds.east},${mapBounds.south}`;
          params.viewbox = viewbox;
          params.bounded = '1';
        }
        geocodeAndCenter(params);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery, mapBounds, selectedCountryCode, selectedCityName]);

  // Center map when country/city selection changes
  useEffect(() => {
    const countryName = countries.find((c) => c.code === selectedCountryCode)?.name || '';
    if (selectedCityName && selectedCountryCode) {
      geocodeAndCenter({ q: `${selectedCityName}, ${countryName}` }, false);
    } else if (selectedCountryCode) {
      geocodeAndCenter({ q: countryName }, false);
    }
  }, [selectedCountryCode, selectedCityName, countries]);

  // Helper: deterministic jitter around a center to avoid overlapping markers
  function hashString(str) {
    let h = 0;
    for (let i = 0; i < String(str).length; i++) {
      h = (h << 5) - h + String(str).charCodeAt(i);
      h |= 0;
    }
    return Math.abs(h);
  }
  const offsetPosition = useCallback((center, key) => {
    if (!center) return undefined;
    const h = hashString(key);
    const angle = (h % 360) * (Math.PI / 180);
    const radius = 0.003 + (h % 100) / 100000;
    const dLat = Math.sin(angle) * radius;
    const dLng = Math.cos(angle) * radius;
    return { lat: center.lat + dLat, lng: center.lng + dLng };
  }, []);

  // Augment creatives with skills and categories derived from related APIs
  async function augmentCreatives(baseCreatives) {
    try {
      const ids = (Array.isArray(baseCreatives) ? baseCreatives : []).map((t) => t.id).filter(Boolean);
      if (ids.length === 0) return baseCreatives;
      const [csRes, skillsRes, servicesRes, catsRes] = await Promise.all([
        apiGet('/creative-skills'),
        apiGet('/skills'),
        apiGet('/services'),
        apiGet('/categories'),
      ]);
      const csList = csRes?.data?.creative_skills || csRes?.creative_skills || csRes?.data || csRes || [];
      const skillsList = skillsRes?.data?.skills || skillsRes?.skills || skillsRes?.data || skillsRes || [];
      const servicesList = servicesRes?.data?.services || servicesRes?.services || servicesRes?.data || servicesRes || [];
      const categoriesList = catsRes?.data?.categories || catsRes?.categories || catsRes?.data || catsRes || [];

      const skillNameById = new Map();
      (Array.isArray(skillsList) ? skillsList : []).forEach((s) => {
        if (s && (s.id != null)) skillNameById.set(s.id, s.name || s.title || String(s));
      });

      const categoryNameById = new Map();
      (Array.isArray(categoriesList) ? categoriesList : []).forEach((c) => {
        if (c && (c.id != null)) categoryNameById.set(c.id, c.name || String(c));
      });

      const skillNamesByCreative = new Map();
      (Array.isArray(csList) ? csList : []).forEach((row) => {
        const cid = row?.creative_id;
        const sid = row?.skill_id;
        if (!cid || !ids.includes(cid)) return;
        const name = skillNameById.get(sid) || row?.skill_name || '';
        if (!name) return;
        const arr = skillNamesByCreative.get(cid) || [];
        if (!arr.includes(name)) arr.push(name);
        skillNamesByCreative.set(cid, arr);
      });

      const serviceTitlesByCreative = new Map();
      const categoryNamesByCreative = new Map();
      (Array.isArray(servicesList) ? servicesList : []).forEach((svc) => {
        const cid = svc?.creative_id;
        if (!cid || !ids.includes(cid)) return;
        const title = svc?.title || svc?.name || '';
        if (title) {
          const arr = serviceTitlesByCreative.get(cid) || [];
          arr.push(title);
          serviceTitlesByCreative.set(cid, arr);
        }
        const catId = svc?.category_id;
        const catName = categoryNameById.get(catId);
        if (catName) {
          const carr = categoryNamesByCreative.get(cid) || [];
          if (!carr.includes(catName)) carr.push(catName);
          categoryNamesByCreative.set(cid, carr);
        }
      });

      return (baseCreatives || []).map((t) => {
        const skills = skillNamesByCreative.get(t.id) || t.skills || [];
        const services = serviceTitlesByCreative.get(t.id) || t.services || [];
        const categories = categoryNamesByCreative.get(t.id) || t.categories || [];
        const category = t.category || (Array.isArray(categories) && categories.length > 0 ? categories[0] : undefined);
        const servicesCount = Array.isArray(services) ? services.length : (t.servicesCount || 0);
        return { ...t, skills, services, servicesCount, categories, category };
      });
    } catch (e) {
      void e;
      return baseCreatives;
    }
  }

  // Positioning: rely only on actual coordinates or geocoded precise positions
  const positionedCreatives = useMemo(() => creatives, [creatives]);

  const filteredCreatives = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return positionedCreatives.filter((t) => {
      // Do not exclude unverified creatives here; allow address-based markers to appear
      const haystack = [
        t.name,
        t.title,
        t.location,
        t.address,
        t.bio,
        t.category,
        ...(Array.isArray(t.categories) ? t.categories : []),
        ...(Array.isArray(t.skills) ? t.skills : []),
        ...(Array.isArray(t.services) ? t.services : []),
        t.user?.display_name,
        [t.user?.firstName, t.user?.lastName].filter(Boolean).join(' '),
        t.user?.email,
        t.user?.phone,
        t.user?.username,
        t.user?.city,
        t.user?.country,
      ].map((x) => fold(String(x || '').toLowerCase()));
      const matchesSearch = !q || haystack.some((x) => x && x.includes(q));

      // If dataset lacks skills, do not over-filter
      const hasAnySkills = positionedCreatives.some((x) => Array.isArray(x.skills) && x.skills.length > 0);
      const matchesSkills = selectedSkills.length === 0 || !hasAnySkills || selectedSkills.every((s) => (t.skills || []).map((x) => (x || '').toLowerCase()).includes(s.toLowerCase()));

      const price = Number(t.hourlyRate ?? 0);
      const matchesPrice = price >= priceRange[0] && price <= priceRange[1];

      const matchesRating = (t.rating ?? 0) >= minRating;

      const hasAnyCategory = positionedCreatives.some((x) => (!!x.category) || (Array.isArray(x.categories) && x.categories.length > 0));
      let matchesCategory = true;
      if (hasAnyCategory) {
        const tCats = (Array.isArray(t.categories) ? t.categories : []).map((c) => String(c).toLowerCase());
        const tCatSingle = String(t.category || '').toLowerCase();
        if (selectedSubCategoryId) {
          const child = categoryById.get(String(selectedSubCategoryId));
          const childName = String(child?.name || '').toLowerCase();
          matchesCategory = !childName || tCats.includes(childName) || (!!tCatSingle && tCatSingle === childName);
        } else if (selectedParentCategoryId) {
          const parent = categoryById.get(String(selectedParentCategoryId));
          const parentName = String(parent?.name || '').toLowerCase();
          const children = (childrenByParentId.get(String(selectedParentCategoryId)) || []).map((c) => String(c.name || '').toLowerCase());
          const allNames = [parentName, ...children].filter(Boolean);
          matchesCategory = allNames.length === 0 || tCats.some((n) => allNames.includes(n)) || (!!tCatSingle && allNames.includes(tCatSingle));
        } else if (selectedCategory) {
          const sel = selectedCategory.toLowerCase();
          matchesCategory = tCats.includes(sel) || (!!tCatSingle && tCatSingle === sel);
        }
      }

      // Country filter: match against ISO codes or country names
      const countrySelCode = fold(String(selectedCountryCode || '').trim().toLowerCase());
      const countrySelName = fold((countries.find((c) => String(c?.code || '').toLowerCase() === String(selectedCountryCode || '').toLowerCase())?.name || '').trim().toLowerCase());
      let matchesCountry = true;
      if (countrySelCode) {
        const candidatesRaw = [
          t.user?.country_code,
          t.country_code,
          t.location?.country,
          t.country,
        ];
        const candidates = candidatesRaw.map((x) => fold(String(x || '').trim().toLowerCase()));
        const addressTokens = String(formatFullAddress(t) || '')
          .split(',')
          .map((p) => fold(p.trim().toLowerCase()))
          .filter(Boolean);
        matchesCountry = candidates.some((c) => c === countrySelCode || c === countrySelName)
          || addressTokens.some((tok) => tok === countrySelName || tok.includes(countrySelName));
      }

      // City filter: fuzzy match on known city fields (accent-folded)
      const citySel = fold(String(selectedCityName || '').trim().toLowerCase());
      let matchesCity = true;
      if (citySel) {
        const cityCandidates = [
          t.city?.name,
          t.city_name,
          t.city,
          t.location?.city?.name,
          t.location?.city,
          t.user?.city,
          t.user?.city_name,
          t.location?.city_name,
          t.address,
          t.location,
        ].map((x) => fold(String(x || '').trim().toLowerCase()));
        const hasCityData = cityCandidates.some((c) => !!c);
        matchesCity = cityCandidates.some((c) => c && c.includes(citySel)) || (!hasCityData && matchesCountry);
      }

      return matchesSearch && matchesSkills && matchesPrice && matchesRating && matchesCategory && matchesCountry && matchesCity;
    });
  }, [positionedCreatives, searchQuery, selectedSkills, priceRange, minRating, selectedCategory, selectedParentCategoryId, selectedSubCategoryId, selectedCountryCode, selectedCityName, countries, categoryById, childrenByParentId]);

  // Overlay precise geocoded positions (if present) onto filtered creatives
  const creativesWithPositions = useMemo(() => {
    let center = null;
    if (selectedCityName) {
      const cityObj = cities.find((c) => String(c?.name || '').toLowerCase() === String(selectedCityName).toLowerCase());
      if (cityObj && Number.isFinite(Number(cityObj.latitude)) && Number.isFinite(Number(cityObj.longitude))) {
        center = { lat: Number(cityObj.latitude), lng: Number(cityObj.longitude) };
      }
    }
    if (!center && mapCenter && Number.isFinite(mapCenter.lat) && Number.isFinite(mapCenter.lng)) {
      center = mapCenter;
    }

    return filteredCreatives.map((t) => {
      const key = t.id || `${t.name}-${t.address}`;
      const precise = key && precisePositions[key];
      if (precise) return { ...t, position: precise };

      // If missing coordinates and we have a center, place talent near center deterministically
      const hasCoords = t.position && typeof t.position.lat === 'number' && typeof t.position.lng === 'number';
      if (!hasCoords && center) {
        const approx = offsetPosition(center, key);
        if (approx) return { ...t, position: approx, positionApproximate: true };
      }
      return t;
    });
  }, [filteredCreatives, precisePositions, selectedCityName, cities, mapCenter, offsetPosition]);

  // In-bounds filtering and position assignment handled in finalVisibleCreatives

  // Batch precise geocoding for creatives missing coords; prefer structured address fields
  useEffect(() => {
    if (geocodingBusy) return;
    const toGeocode = positionedCreatives.filter((t) => {
      const key = t.id || `${t.name}-${t.address}`;
      const cached = key && precisePositions[key];
      const hasCoords = t.position && typeof t.position.lat === 'number' && typeof t.position.lng === 'number';
      const fullAddress = formatFullAddress(t);
      return !cached && !hasCoords && Boolean(fullAddress);
    }).slice(0, 5); // limit per tick to avoid hammering API
    if (toGeocode.length === 0) return;

    let cancelled = false;
    setGeocodingBusy(true);
    (async () => {
      for (const candidate of toGeocode) {
        if (cancelled) break;
        const key = candidate.id || `${candidate.name}-${candidate.address}`;
        const freeQuery = formatFullAddress(candidate);
        const parsed = parseAddressParts(freeQuery);
        const postalGuessMatch = freeQuery.match(/\b\d{3}\s?\d{2}\b/); // e.g., 111 21
        const postalGuess = (postalGuessMatch && postalGuessMatch[0]) || '';
        const tokens = freeQuery.split(',').map((s) => String(s).trim());
        const lastToken = tokens[tokens.length - 1] || '';
        const countryGuess = /^[A-Za-z]{2}$/.test(lastToken) ? lastToken.toLowerCase() : '';

        const streetLine = candidate.address_line || candidate.address || candidate.location?.address_line || candidate.user?.address_line || parsed.street || '';
        const cityLine = candidate.city?.name || candidate.city_name || candidate.location?.city?.name || parsed.city || '';
        const postalLine = candidate.postal_code || candidate.user?.postal_code || parsed.postal || postalGuess || '';
        const countryCode = (candidate.country_code || candidate.location?.country || candidate.user?.country_code || selectedCountryCode || countryGuess || '').toString().toLowerCase();

        // Prefer free-form query always (handles floor/apartment tokens better)
        const params = { q: freeQuery };
        if (streetLine) params.street = streetLine;
        if (cityLine) params.city = cityLine;
        if (postalLine) params.postalcode = postalLine;
        if (countryCode) params.countrycodes = countryCode;
        // Bias results to current viewport when available
        if (mapBounds) {
          const viewbox = `${mapBounds.west},${mapBounds.north},${mapBounds.east},${mapBounds.south}`;
          params.viewbox = viewbox;
          params.bounded = '1';
        }
        logger.debug('[Geocode] queue:', key, { streetLine, cityLine, postalLine, countryCode, q: params.q || freeQuery });
        try {
          const res = await apiGet('/geocode', params);
          const r = res?.data || res;
          const lat = Number(r?.lat ?? r?.latitude);
          const lng = Number(r?.lon ?? r?.lng ?? r?.longitude);
          if (!cancelled && Number.isFinite(lat) && Number.isFinite(lng)) {
            setPrecisePositions((prev) => ({ ...prev, [key]: { lat, lng } }));
          }
          logger.debug('[Geocode] result:', key, lat, lng, r?.display_name || r);
          // small delay between requests
          await new Promise((resolve) => setTimeout(resolve, 300));
        } catch (e) {
          void e;
          // If structured+q failed once, try pure free query without structured parts
          try {
            const res2 = await apiGet('/geocode', { q: freeQuery, countrycodes: countryCode || undefined });
            const r2 = res2?.data || res2;
            const lat2 = Number(r2?.lat ?? r2?.latitude);
            const lng2 = Number(r2?.lon ?? r2?.lng ?? r2?.longitude);
            if (!cancelled && Number.isFinite(lat2) && Number.isFinite(lng2)) {
              setPrecisePositions((prev) => ({ ...prev, [key]: { lat: lat2, lng: lng2 } }));
            }
            logger.debug('[Geocode] fallback result:', key, lat2, lng2, r2?.display_name || r2);
          } catch (__) {
            void __;
          }
        }
      }
      if (!cancelled) setGeocodingBusy(false);
    })();
    return () => { cancelled = true; };
  }, [positionedCreatives, filteredCreatives, precisePositions, geocodingBusy, selectedCountryCode, selectedCityName, searchQuery, mapBounds]);

  const finalVisibleCreatives = useMemo(() => {
    return creativesWithPositions.map((t) => {
      const key = t.id || `${t.name}-${t.address}`;
      const precise = key && precisePositions[key];
      if (precise) {
        // Mark approximate if the talent lacked original coordinates and we used geocoded ones
        const wasMissingCoords = !(t.position && typeof t.position.lat === 'number' && typeof t.position.lng === 'number');
        return { ...t, position: precise, positionApproximate: wasMissingCoords };
      }
      return t;
    }).filter((t) => t.position && Number.isFinite(t.position.lat) && Number.isFinite(t.position.lng));
  }, [creativesWithPositions, precisePositions]);

  const sortedCreatives = finalVisibleCreatives; // placeholder for sort/filter expansion

  // Debug: log counts to ensure positions are being computed
  useEffect(() => {
    try {
      const withCoords = creatives.filter((t) => t.position && typeof t.position.lat === 'number' && typeof t.position.lng === 'number').length;
      logger.debug('[CreativeMap] creatives:', creatives.length, 'with position:', withCoords, 'visible markers:', sortedCreatives.length);
    } catch (e) { void e; }
  }, [creatives, sortedCreatives]);

  // Auto-center or fit bounds to make visible markers appear in view
  useEffect(() => {
    // Skip auto-centering while user is actively searching or a recent manual center occurred
    const searching = Boolean(searchQuery.trim());
    if (searching) return;
    if (centerLockedUntil && Date.now() < centerLockedUntil) return;
    if (!mapRef.current || !Array.isArray(sortedCreatives)) return;
    const markers = sortedCreatives.filter((t) => t.position && Number.isFinite(t.position.lat) && Number.isFinite(t.position.lng));
    if (markers.length === 0) return;
    try {
      if (markers.length === 1) {
        const p = markers[0].position;
        mapRef.current.setCenter(p);
        setMapCenter(p);
      } else {
        const bounds = new window.google.maps.LatLngBounds();
        markers.forEach((m) => bounds.extend(m.position));
        mapRef.current.fitBounds(bounds);
      }
    } catch (e) { void e; }
  }, [selectedCountryCode, selectedCityName, sortedCreatives, centerLockedUntil, searchQuery]);

  // When searching, center/fit to matching markers so results are in view
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) return;
    if (!mapRef.current || !Array.isArray(sortedCreatives)) return;
    const markers = sortedCreatives.filter((t) => t.position && Number.isFinite(t.position.lat) && Number.isFinite(t.position.lng));
    if (markers.length === 0) return;
    const run = () => {
      try {
        if (markers.length === 1) {
          const p = markers[0].position;
          if (p && Number.isFinite(p.lat) && Number.isFinite(p.lng)) {
            mapRef.current.setCenter(p);
            setMapCenter(p);
          }
        } else {
          const bounds = new window.google.maps.LatLngBounds();
          markers.forEach((m) => bounds.extend(m.position));
          mapRef.current.fitBounds(bounds);
        }
        setCenterLockedUntil(Date.now() + 20000);
      } catch (e) { void e; }
    };
    const timer = setTimeout(run, 450);
    return () => clearTimeout(timer);
  }, [searchQuery, sortedCreatives]);

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const userLocation = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setMapCenter(userLocation);
          if (mapRef.current) {
            mapRef.current.panTo(userLocation);
            mapRef.current.setZoom(13);
          }
          // Lock auto-centering briefly to avoid reset after my-location
          setCenterLockedUntil(Date.now() + 20000);
          // Leaflet removed
        },
        (err) => { logger.warn('Geolocation error', err); }
      );
    }
  };

  const geocodeAndCenter = async (params, lockCenter = true) => {
    try {
      const res = await apiGet('/geocode', params);
      const result = res?.data || res;
      const lat = Number(result?.lat ?? result?.latitude);
      const lng = Number(result?.lon ?? result?.lng ?? result?.longitude);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        const center = { lat, lng };
        setMapCenter(center);
        if (mapRef.current) {
          mapRef.current.panTo(center);
          mapRef.current.setZoom(13);
        }
        // Lock auto-centering briefly so periodic data refresh or marker updates don’t override
        if (lockCenter) setCenterLockedUntil(Date.now() + 20000);
        // Leaflet removed
      }
    } catch (err) {
      logger.warn('Geocode failed', err);
    }
  };

  
  if (hasApiKey && loadError) {
    const msg = mapsError || String(loadError?.message || loadError || '');
    return (
      <div className="p-4 rounded border bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-200">
        <div className="font-semibold mb-1">Error loading Google Maps</div>
        <div className="text-sm">
          {msg || 'Check that your API key is valid and unrestricted for localhost.'}
        </div>
        <div className="text-xs mt-2">
          Common causes: invalid key, Maps JavaScript API not enabled, missing billing, or HTTP referrer restrictions.
        </div>
      </div>
    );
  }
  if (hasApiKey && !isLoaded) return <div className="w-full h-[600px] bg-zinc-100 dark:bg-zinc-800 animate-pulse rounded" />;

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-2">
        <div className="flex items-center relative flex-1">
          <input
            type="text"
            placeholder={t('talentMap.ui.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-3 pr-3 py-2 flex-1 rounded border dark:border-zinc-700 bg-white dark:bg-zinc-900 dark:text-white"
          />
        </div>
        {/* Scope dropdown removed as requested */}

        {/* Country → City hierarchical dropdowns */}
        <select
          value={selectedCountryCode}
          onChange={(e) => { setSelectedCountryCode(e.target.value); setSelectedCityName(''); }}
          className="px-3 py-2 rounded border dark:border-zinc-700 bg-white dark:bg-zinc-900 min-w-[160px]"
        >
          <option value="">{loadingCountries ? 'Loading countries…' : 'Select country'}</option>
          {countries.map((c) => (
            <option key={c.code} value={c.code}>{c.name}</option>
          ))}
        </select>

        <select
          value={selectedCityName}
          onChange={(e) => setSelectedCityName(e.target.value)}
          disabled={!selectedCountryCode}
          className="px-3 py-2 rounded border dark:border-zinc-700 bg-white dark:bg-zinc-900 min-w-[160px] disabled:opacity-50"
        >
          <option value="">{!selectedCountryCode ? 'Select city' : (loadingCities ? 'Loading cities…' : 'Select city')}</option>
          {cities.map((city) => (
            <option key={city.id || city.name} value={city.name}>{city.name}</option>
          ))}
        </select>

        {/* Verified-only is always enforced; toggle removed */}

        <button onClick={getUserLocation} className="btn btn-outline border dark:border-zinc-700 dark:text-zinc-300 p-2" aria-label="My Location">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v4"/><path d="M12 18v4"/><path d="M2 12h4"/><path d="M18 12h4"/><circle cx="12" cy="12" r="3"/></svg>
        </button>

        <button onClick={() => setIsFilterOpen(true)} className="btn btn-outline border dark:border-zinc-700 dark:text-zinc-300 p-2" aria-label="Filters">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="21" y1="4" x2="14" y2="4"/><line x1="10" y1="4" x2="3" y2="4"/><line x1="21" y1="12" x2="12" y2="12"/><line x1="8" y1="12" x2="3" y2="12"/><line x1="21" y1="20" x2="16" y2="20"/><line x1="12" y1="20" x2="3" y2="20"/><circle cx="12" cy="4" r="2"/><circle cx="8" cy="12" r="2"/><circle cx="16" cy="20" r="2"/></svg>
        </button>
      </div>

      {/* Off-canvas Filters Panel */}
      {isFilterOpen && (
        <div className="fixed inset-0 z-2000">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 dark:bg-black/60"
            onClick={() => setIsFilterOpen(false)}
          />
          {/* Panel */}
          <div className="absolute right-0 top-0 h-full w-full max-w-[380px] section-bg border-l dark:border-zinc-700 shadow-xl flex flex-col z-2001">
            <div className="p-4 flex items-center justify-between border-b dark:border-zinc-700">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-[#1756fd] text-white flex items-center justify-center">🔎</div>
                <div className="font-semibold text-primary-text">{t('talentMap.ui.filters.label', { defaultValue: 'Filters' })}</div>
              </div>
              <button
                onClick={() => setIsFilterOpen(false)}
                className="icon-btn w-10 h-10"
                aria-label="Close filters"
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-6">
              {/* Active Filters Summary */}
              <div className="bg-card-bg card-hover rounded-md p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-primary-text">Active Filters</div>
                  <button
                    className="text-xs text-zinc-600 dark:text-zinc-300 hover:underline"
                    onClick={() => { setSelectedSkills([]); setPriceRange([0,10000]); setMinRating(0); setSelectedCategory(''); setSelectedParentCategoryId(''); setSelectedSubCategoryId(''); }}
                  >Clear all</button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedSkills.map((skill) => (
                    <button
                      key={`sel-skill-${skill}`}
                      onClick={() => setSelectedSkills((prev) => prev.filter((x) => x !== skill))}
                      className="text-xs px-2 py-1 rounded-full bg-[#1756fd] text-white hover:opacity-90"
                    >{skill} ✕</button>
                  ))}
                  {(priceRange[0] !== 0 || priceRange[1] !== 10000) && (
                    <button
                      onClick={() => setPriceRange([0,10000])}
                      className="text-xs px-2 py-1 rounded-full border dark:border-zinc-700 bg-white dark:bg-zinc-900"
                    >{priceRange[0]}–{priceRange[1]} ✕</button>
                  )}
                  {minRating > 0 && (
                    <button
                      onClick={() => setMinRating(0)}
                      className="text-xs px-2 py-1 rounded-full border dark:border-zinc-700 bg-white dark:bg-zinc-900"
                    >{minRating}★ ✕</button>
                  )}
                  {selectedCategory && (
                    <button
                      onClick={() => setSelectedCategory('')}
                      className="text-xs px-2 py-1 rounded-full border dark:border-zinc-700 bg-white dark:bg-zinc-900"
                    >{selectedCategory} ✕</button>
                  )}
                </div>
              </div>

              {/* Skills */}
              <div className="bg-card-bg card-hover rounded-md p-3">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-medium text-primary-text">{t('talentMap.ui.filters.skills')}</div>
                  <input
                    value={skillSearchTerm}
                    onChange={(e) => setSkillSearchTerm(e.target.value)}
                    placeholder="Search skills"
                    className="text-sm px-2 py-1 rounded border dark:border-zinc-700 bg-white dark:bg-zinc-900 w-40"
                  />
                </div>
                {loadingFilters && skillsOptions.length === 0 ? (
                  <div className="text-sm text-secondary-text">Loading...</div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {(skillsOptions.length > 0 ? skillsOptions.map((s) => s.name || s) : [...new Set(creatives.flatMap((t) => t.skills || []))])
                      .filter((s) => !skillSearchTerm || String(s).toLowerCase().includes(skillSearchTerm.toLowerCase()))
                      .slice(0, 36)
                      .map((skill) => {
                        const active = selectedSkills.includes(skill);
                        return (
                          <button
                            key={skill}
                            onClick={() => setSelectedSkills((prev) => active ? prev.filter((x) => x !== skill) : [...prev, skill])}
                      className={`text-sm px-3 py-1 rounded-full border dark:border-zinc-700 ${active ? 'bg-[#1756fd] text-white' : 'bg-white dark:bg-zinc-900'} hover:shadow`}
                          >
                            {active ? '✓ ' : ''}{skill}
                          </button>
                        );
                      })}
                  </div>
                )}
              </div>

              {/* Price Range */}
              <div className="bg-card-bg card-hover rounded-md p-3">
                <div className="font-medium mb-2 text-primary-text">{t('talentMap.ui.filters.priceRange')}</div>
                <div className="flex items-center gap-2 mb-2">
                  <input type="number" value={priceRange[0]} min={0} max={priceRange[1]}
                    onChange={(e) => setPriceRange([Number(e.target.value), priceRange[1]])}
                    className="w-24 px-2 py-1 rounded border dark:border-zinc-700 bg-white dark:bg-zinc-900" />
                  <span>—</span>
                  <input type="number" value={priceRange[1]} min={priceRange[0]} max={10000}
                    onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value)])}
                    className="w-24 px-2 py-1 rounded border dark:border-zinc-700 bg-white dark:bg-zinc-900" />
                </div>
                <div className="flex flex-wrap gap-2">
                  {[ [0,500], [500,1000], [1000,3000], [3000,10000] ].map(([min,max]) => {
                    const active = priceRange[0] === min && priceRange[1] === max;
                    return (
                      <button
                        key={`range-${min}-${max}`}
                        onClick={() => setPriceRange([min,max])}
                      className={`text-xs px-3 py-1 rounded-full border dark:border-zinc-700 ${active ? 'bg-[#1756fd] text-white' : 'bg-white dark:bg-zinc-900'}`}
                      >{min}–{max}</button>
                    );
                  })}
                </div>
              </div>

              {/* Rating */}
              <div className="bg-card-bg card-hover rounded-md p-3">
                <div className="font-medium mb-2 text-primary-text">{t('talentMap.ui.filters.rating')}</div>
                <div className="flex items-center gap-2">
                  {[0,1,2,3,4,5].map((r) => {
                    const active = minRating === r;
                    return (
                      <button
                        key={`rating-${r}`}
                        onClick={() => setMinRating(r)}
                      className={`text-sm px-2 py-1 rounded border dark:border-zinc-700 ${active ? 'bg-[#1756fd] text-white' : 'bg-white dark:bg-zinc-900'}`}
                      >{r}★</button>
                    );
                  })}
                </div>
              </div>

              {/* Category (Parent/Subcategory Dropdowns) */}
              <div className="bg-card-bg card-hover rounded-md p-3">
                <div className="font-medium mb-2 text-primary-text">{t('talentMap.ui.filters.category')}</div>
                {loadingFilters && categoryOptions.length === 0 ? (
                  <div className="text-sm text-secondary-text">Loading...</div>
                ) : (
                  <div className="space-y-2">
                    <select
                      value={selectedParentCategoryId}
                      onChange={(e) => { setSelectedParentCategoryId(e.target.value); setSelectedSubCategoryId(''); setSelectedCategory(''); }}
                      className="w-full px-3 py-2 rounded border dark:border-zinc-700 bg-white dark:bg-zinc-900"
                    >
                      <option value="">Select main category</option>
                      {parentCategories.map((pc) => (
                        <option key={pc.id} value={pc.id}>{pc.name}</option>
                      ))}
                    </select>
                    <select
                      value={selectedSubCategoryId}
                      onChange={(e) => { setSelectedSubCategoryId(e.target.value); setSelectedCategory(''); }}
                      disabled={!selectedParentCategoryId || (childrenByParentId.get(String(selectedParentCategoryId)) || []).length === 0}
                      className="w-full px-3 py-2 rounded border dark:border-zinc-700 bg-white dark:bg-zinc-900 disabled:opacity-50"
                    >
                      <option value="">{selectedParentCategoryId ? 'Select subcategory' : 'Select a main category first'}</option>
                      {(childrenByParentId.get(String(selectedParentCategoryId)) || []).map((child) => (
                        <option key={child.id} value={child.id}>{child.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 border-t dark:border-zinc-700 flex items-center gap-2">
              <button
                className="btn btn-primary"
                onClick={() => { setIsFilterOpen(false); }}
              >
                {t('talentMap.ui.filters.apply', { defaultValue: 'Apply' })}
              </button>
              <button
                className="btn btn-outline"
                onClick={() => { setSelectedSkills([]); setPriceRange([0,10000]); setMinRating(0); setSelectedCategory(''); setSelectedParentCategoryId(''); setSelectedSubCategoryId(''); }}
              >
                {t('talentMap.ui.filters.clear', { defaultValue: 'Clear' })}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="text-sm text-secondary-text">
        {t('talentMap.ui.showingCount', { count: sortedCreatives.length })}
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="section-bg rounded-lg overflow-hidden shadow-lg border dark:border-zinc-700 relative flex-1">
          {loading && (
          <div className="absolute top-2 right-2 z-10 bg-[#1756fd] text-white py-1 px-3 rounded-full text-sm">
              Loading creatives...
            </div>
          )}

          {sortedCreatives.length === 0 && !loading && (
            <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
              <div className="pointer-events-auto text-center p-4 rounded-lg shadow-lg border bg-white/90 dark:bg-zinc-900/90 dark:border-zinc-700">
                <div className="font-semibold mb-1 text-primary-text">Creative not found</div>
                <div className="text-sm text-secondary-text">Try adjusting filters or selecting a different location.</div>
              </div>
            </div>
          )}

          {/* Warn if Google Maps API key is missing */}
          {!hasApiKey && (
            <div className="absolute top-2 left-2 z-10 bg-red-50 text-red-700 border border-red-200 px-3 py-1 rounded text-sm">
              Google Maps API key missing; map cannot render.
            </div>
          )}

          {hasApiKey ? (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              zoom={14}
              center={mapCenter}
              onLoad={onMapLoad}
              onBoundsChanged={onBoundsChanged}
              options={{
                fullscreenControl: false,
                streetViewControl: false,
                mapTypeControl: false,
                zoomControl: true,
                styles: [
                  { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
                  { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] },
                  { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
                  { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
                ],
              }}
            >
              {/* Show info if some positions are approximate due to missing coordinates */}
              {finalVisibleCreatives.some((t) => t.positionApproximate) && (
                <div className="absolute top-2 left-2 z-10 bg-white/90 dark:bg-zinc-800/90 text-xs px-2 py-1 rounded shadow">
                  Some markers are approximate (placed near selected city).
                </div>
              )}
              {sortedCreatives.map((creative) => (
                <PriceTag
                  key={creative.id || `${creative.name}-${creative.address}`}
                  creative={creative}
                  isSelected={selectedCreative?.id === creative.id}
                  onClick={() => setSelectedCreative(creative)}
                  formatAmount={formatAmount}
                />
              ))}
            </GoogleMap>
          ) : (
            <div className="w-full h-[600px] flex items-center justify-center bg-zinc-100 dark:bg-zinc-800">
              <div className="text-center p-4">
                <div className="font-semibold mb-1">Google Maps preview</div>
                <div className="text-sm text-zinc-600 dark:text-zinc-300">API key not set. Map disabled for now.</div>
                <div className="mt-3 text-xs text-zinc-500 dark:text-zinc-400">Add VITE_GOOGLE_MAPS_API_KEY in .env to enable full map.</div>
              </div>
            </div>
          )}
        </div>

        {selectedCreative && (
          <div className="w-full md:w-80 h-auto min-h-[360px] bg-card-bg card-hover rounded-xl shadow-md border dark:border-zinc-700 flex flex-col relative z-10">
            {/* Close button (top-right) */}
            <button
              onClick={() => setSelectedCreative(null)}
              className="icon-btn w-10 h-10 absolute right-2 top-2"
              aria-label="Close details"
              title="Close"
            >
              ✕
            </button>
            {/* Content */}
            <div className="flex-1 p-3 space-y-3">
              <h3 className="text-base font-semibold text-primary-text">Creative Details</h3>
              <div className="flex items-start gap-3">
                {/* Avatar with fallback to initials */}
                {selectedCreative.avatar && selectedCreative.avatar !== '/placeholder.svg' ? (
                  <img src={selectedCreative.avatar} alt={getPublicDisplayName(selectedCreative?.user) || selectedCreative.name || 'Creative'} className="w-16 h-16 rounded-full object-cover" />
                ) : (
          <div className="w-16 h-16 rounded-full bg-[#1756fd] text-white flex items-center justify-center text-xl font-bold">
                    {initialsOf(getPublicDisplayName(selectedCreative?.user) || selectedCreative.name || 'Creative') || 'C'}
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-primary-text">{getPublicDisplayName(selectedCreative?.user) || selectedCreative.name}</h3>
                  {selectedCreative.title && (
                    <p className="text-sm text-secondary-text">{selectedCreative.title}</p>
                  )}
                  <div className="flex items-center text-yellow-500 mt-1">
                    <span>★</span>
                    <span className="text-sm ml-1">{selectedCreative.rating}</span>
                  </div>
                  {/* Removed inline Hourly/Address here to align with sections below */}
              </div>
            </div>

            {/* Hourly and Address aligned with Services/Skills, original inline formatting */}
            <div className="mt-2 space-y-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm text-primary-text">{t('talentMap.ui.hourly')}:</span>
                <span className="text-sm text-secondary-text">{selectedCreative.hourlyRate ? formatAmount(selectedCreative.hourlyRate, selectedCreative.currency) : 'N/A'}</span>
              </div>
              {(selectedCreative.address || selectedCreative.location || selectedCreative.city_name) && (
                <div className="flex items-start gap-2">
                  <span className="font-medium text-sm text-primary-text">{t('talentMap.ui.location', { defaultValue: 'Location' })}:</span>
                  <div className="text-sm text-secondary-text whitespace-normal wrap-break-word">{parseAddressParts(selectedCreative.address || '').city || selectedCreative.city_name || selectedCreative.location || ''}</div>
                </div>
              )}
            </div>

              {/* Bio */}
              {selectedCreative.bio && (
                <div>
                  <div className="font-medium mb-2 text-primary-text">Bio</div>
                  <p className="text-sm text-secondary-text leading-relaxed">{selectedCreative.bio}</p>
                </div>
              )}

              {/* Services list */}
              {Array.isArray(selectedCreative.services) && selectedCreative.services.length > 0 && (
                <div>
                  <div className="font-medium mb-2 text-primary-text">Services</div>
                  <div className="flex flex-wrap gap-2">
                    {selectedCreative.services.slice(0, 20).map((s, idx) => (
                      <span key={`${s}-${idx}`} className="inline-flex items-center rounded-full bg-subtle-bg border border-card-border px-3 py-1 text-xs text-primary-text">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Skills list */}
              {Array.isArray(selectedCreative.skills) && selectedCreative.skills.length > 0 && (
                <div>
                  <div className="font-medium mb-2 text-primary-text">{t('talentMap.ui.filters.skills')}</div>
                  <div className="flex flex-wrap gap-2">
                    {selectedCreative.skills.slice(0, 20).map((s) => (
                      <span key={s} className="inline-flex items-center rounded-full bg-subtle-bg border border-card-border px-3 py-1 text-xs text-primary-text">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {/* Footer CTA (bottom-aligned) */}
            <div className="p-3 mt-auto">
              <Link
                to={(() => {
                  const name = getPublicDisplayName(selectedCreative?.user) || selectedCreative?.name || '';
                  const baseSlug = slugify(name || '') || 'creative';
                  const rawId = selectedCreative?.user?.id || selectedCreative?.id || '';
                  const idText = String(rawId).toLowerCase().replace(/[^0-9a-f]/g, '');
                  const short = `${idText.slice(0, 6)}${idText.slice(-6)}`;
                  const isValidShort = short.length === 12 && /^[a-f0-9]{12}$/.test(short);
                  return isValidShort ? `/creative/profile/${baseSlug}-${short}` : `/creatives/${rawId}`;
                })()}
                className="cta-brand inline-block w-full text-center py-2 px-3"
                data-variant="primary"
              >
                <span className="inline-flex items-center justify-center gap-2">
                  View Profile
                  <FiUser className="w-4 h-4" />
                </span>
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* AI Suggestions removed: integrated with filters/search */}
    </div>
  );
}

// Fold accents/diacritics for robust comparisons (e.g., Malmo vs Malmö)
function fold(s) {
  try {
    return String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  } catch (e) {
    void e;
    return String(s || '');
  }
}
